import React, { useLayoutEffect, useEffect, useState } from 'react';
import moment from 'moment';
import apiClient from '../../../../../../Config/apiClient';
import ClipLoader from 'react-spinners/ClipLoader';

import plusIcon from '../../../../../../Assets/images/plus-icon.svg';
import editIcon from '../../../../../../Assets/images/edit-icon.svg';
import deleteIcon from '../../../../../../Assets/images/delete-icon.svg';
import AddRoleModal from '../AddRoles/AddRolesModal';
import './RolesList.scss';
import { useSidebar } from '../../../Context/SidebarContext/SidebarContext';
import ConfirmationModal from '../../../../../../Components/ConfirmationBox/ConfirmationModal';
import { showSuccessToast, showErrorToast } from '../../../../../../Context/Toaster/Toaster';
import { hasPermission } from '../../../Services/permission';
import { DATE_FORMAT } from '../../../Utils/Constants';

const rolesBaseUrl = process.env.REACT_APP_TC_ROLES_BASE_PATH;

const ROLES_SECTION = 'settings';
const ROLES_EDIT_KEYS = ['settings_edit'];

const RolesList = () => {
  const [rolesData, setRolesData] = useState([]);
  const [rolesPerPage, setRolesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { setActiveSubTab } = useSidebar();

  useLayoutEffect(() => {
    setActiveSubTab("Roles");
    return () => {
      setActiveSubTab("");
    };
  }, []);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${rolesBaseUrl}/list`);
      if (res.data.status === "SUCCESS") {
        const sortedRoles = res.data.data.sort(
          (a, b) => new Date(b.created_datetime) - new Date(a.created_datetime)
        );
        setRolesData(sortedRoles);

        const totalPages = Math.ceil(sortedRoles.length / rolesPerPage);
        if (currentPage > totalPages || totalPages === 0) {
          setCurrentPage(1);
        }
      }
    } catch (error) {
      const msg = error.response ? error.response.data.message : error.message;
      console.error(msg);
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const canEditRole = hasPermission({ section: ROLES_SECTION, keys: ROLES_EDIT_KEYS });

  const deleteRoleById = async (roleId) => {
    try {
      const res = await apiClient.delete(`${rolesBaseUrl}/${roleId}`);
      if (res.data.status === "SUCCESS") {
        showSuccessToast(res.data.message);
        const updatedRoles = rolesData.filter(role => role.id !== roleId);
        setRolesData(updatedRoles);

        const totalAfterDelete = updatedRoles.length;
        const newTotalPages = Math.ceil(totalAfterDelete / rolesPerPage);

        if (newTotalPages === 0) {
          setCurrentPage(1);
        } else if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages);
        }
      } else {
        showErrorToast("Failed to delete permission.");
      }
    } catch (err) {
      const msg = err.response?.data?.error_data?.message || err.message;

      if (err.response?.data?.error_data?.code === "ROLE-IN-USE") {
        showErrorToast("Cannot delete: role is in use by active users.");
      } else {
        showErrorToast(msg);
      }
    } finally {
      setShowConfirmModal(false);
    }
  };

  const totalRoles = rolesData.length;
  const totalPages = Math.ceil(totalRoles / rolesPerPage);
  const startIndex = (currentPage - 1) * rolesPerPage;
  const currentRoles = rolesData.slice(startIndex, startIndex + rolesPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleAddRole = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEditRole = (role) => {
    setEditData(role);
    setShowModal(true);
  };

  const handleDeleteClick = (role) => {
    setSelectedRole(role);
    setShowConfirmModal(true);
  };

  return (
    <main className="full-wraper">
      <div className="main-wraper">
        <div className="container-fluid">
          <div className="main-inner">
            <div className="common-top" style={{ position: "relative" }}>
              {canEditRole && (
                <a href="#" className="post-button permission-button" style={{ float: 'right' }} onClick={handleAddRole}>
                  {/* <img src={plusIcon} alt="Add" />  */}
                  Add Permission
                </a>
              )}
            </div>

            <div className="table-cvr">
              <div className="table-inner">
                <table id="roleTable" className="table">
                  <thead>
                    <tr>
                      <th className="text-middle first-col">#</th>
                      <th className="role-col">Permission</th>
                      <th className="created-col">Created On</th>
                      <th className="edit-col"></th>
                      <th className="delete-col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <ClipLoader size={25} color="gray" />
                        </td>
                      </tr>
                    ) : currentRoles.length > 0 ? (
                      currentRoles.map((role, index) => (
                        <tr key={role.id}>
                          <td className="text-middle">{startIndex + index + 1}</td>
                          <td>{role.role_name}</td>
                          <td>{moment(role.created_datetime).local().format(DATE_FORMAT)}</td>
                          <td className="edit-col">
                            {canEditRole && (
                              <a href="#" onClick={() => handleEditRole(role)}>
                                <img className="tb-icon" src={editIcon} alt="Edit" />
                              </a>
                            )}
                          </td>
                          <td>
                            {canEditRole && (
                              <a href="#" onClick={(e) => { e.preventDefault(); handleDeleteClick(role); }}>
                                <img className="tb-icon" src={deleteIcon} alt="Delete" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">No permissions found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Block */}
              <div className="dt-layout-row d-flex align-items-center justify-content-between">
                <div className="dt-length">
                  Showing{' '}
                  <select
                    className="dt-input"
                    value={rolesPerPage}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setRolesPerPage(Number(e.target.value));
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>{' '}
                  of {totalRoles} entries
                </div>

                <div className="dt-paging d-flex align-items-center" style={{ gap: '5px' }}>
                  <button
                    className="dt-paging-button previous"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i + 1}
                      className={`dt-paging-button ${currentPage === i + 1 ? 'current' : ''}`}
                      onClick={() => handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    className="dt-paging-button next"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <AddRoleModal
          onClose={() => setShowModal(false)}
          editData={editData}
          refreshList={loadRoles}
        />
      )}
      {showConfirmModal && (
        <ConfirmationModal
          id="deleteRoleModal"
          show={showConfirmModal}
          title="Confirm Deletion"
          message={`Are you sure you want to delete the permission "${selectedRole?.role_name}"?`}
          confirmText="Yes"
          cancelText="Cancel"
          onConfirm={() => deleteRoleById(selectedRole?.id)}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

    </main>
  );
};

export default RolesList;
